import { productDataBaseUrl } from '@/lib/api/product-data-client'

/**
 * The customer banner the API Worker opens while a Cloudflare incident hits a component we use AND we
 * measurably fail on it (`GET /api/public/platform-status`, plan
 * `docs/superpowers/plans/2026-10-29-platform-status-banner.md`). The API closes it by itself when the
 * incident resolves, or when staff hide it from the admin console.
 */
export interface PlatformStatusBannerData {
  /** Written for the customer; already says how to recover. */
  message: string
  /** Our measured components the incident hits (`durable_objects`, `d1`). */
  components: string[]
  /** When the banner opened (ISO). A customer who closes it hides it for this `since` only. */
  since: string
}

/** How often an open page reads the status again. */
export const PLATFORM_STATUS_POLL_MS = 2 * 60 * 1000

const PLATFORM_STATUS_PATH = '/api/public/platform-status'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The banner in a `GET /api/public/platform-status` body, or null for anything else. */
export function parsePlatformStatus(body: unknown): PlatformStatusBannerData | null {
  if (!isRecord(body) || body.success !== true || !isRecord(body.data)) return null
  const banner = body.data.banner
  if (!isRecord(banner)) return null
  const { message, since, components } = banner
  if (typeof message !== 'string' || !message.trim()) return null
  if (typeof since !== 'string' || !since) return null
  if (!Array.isArray(components)) return null
  return {
    message: message.trim(),
    since,
    components: components.filter((c): c is string => typeof c === 'string'),
  }
}

/**
 * Reads the public route: no session, no cookies (it shows on signed-out pages too). Any failure
 * (not configured, network, non-2xx, a body we cannot read) answers null — no banner, never a throw,
 * since a status read must never break the page it sits on.
 */
export async function fetchPlatformStatus(signal?: AbortSignal): Promise<PlatformStatusBannerData | null> {
  try {
    const response = await fetch(`${productDataBaseUrl()}${PLATFORM_STATUS_PATH}`, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal,
    })
    if (!response.ok) return null
    return parsePlatformStatus(await response.json())
  } catch {
    return null
  }
}
